import { defineStore } from 'pinia'
import { constantRouterMap } from '@/router'
import { store } from '../index'
import { useUserStore } from './user'

export interface PermissionState {
  /** 已注册的"可见路由"全集（含 system 三个子页面，在 constantRouterMap 中已声明） */
  routers: AppRouteRecordRaw[]
  /** 兼容旧接口，目前等同于 routers */
  addRouters: AppRouteRecordRaw[]
  /** 是否已完成路由注册（本方案下静态路由固定为 true） */
  isAddRouters: boolean
}

/**
 * 按权限码过滤路由：路由 meta.permission 中**任一** code 命中即视为可见；
 * 缺省或空数组视为无限制。
 */
export const filterByPermission = (
  routes: AppRouteRecordRaw[],
  codes: readonly string[]
): AppRouteRecordRaw[] =>
  routes
    .filter((route) => {
      const required = route.meta?.permission as
        | string
        | readonly string[]
        | undefined
      if (!required) return true
      const list = Array.isArray(required) ? required : [required]
      if (list.length === 0) return true
      return list.some((code) => codes.includes(code))
    })
    .map((route) => ({
      ...route,
      children: route.children ? filterByPermission(route.children, codes) : route.children
    }))

export const usePermissionStore = defineStore('permission', {
  state: (): PermissionState => ({
    routers: constantRouterMap,
    addRouters: [],
    isAddRouters: true
  }),

  getters: {
    /** 经权限过滤 + hidden 过滤后的路由集合，供菜单/侧栏使用 */
    visibleRouters(state): AppRouteRecordRaw[] {
      const codes = useUserStore().permissionCodes
      return filterByPermission(state.routers, codes).filter((route) => !route.meta?.hidden)
    }
  },

  actions: {
    setIsAddRouters(value: boolean) {
      this.isAddRouters = value
    },
    reset() {
      this.routers = constantRouterMap
      this.addRouters = []
      this.isAddRouters = true
    }
  }
})

export const usePermissionStoreWithOut = () => usePermissionStore(store)